import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    FiLock, FiCheckCircle, FiClock, FiUser, FiAward,
    FiZap, FiMessageCircle, FiArrowRight, FiDownload, FiTrash2
} from 'react-icons/fi';
import api, { RUTA_ARCHIVOS } from '../api/client';
import Estrellas from '../components/Estrellas';
import { useAuth } from '../context/AuthContext';

export default function DetalleCurso() {
    const { id } = useParams();
    const { usuario } = useAuth();
    const [curso, setCurso] = useState(null);
    const [videos, setVideos] = useState([]);
    const [calificaciones, setCalificaciones] = useState([]);
    const [stats, setStats] = useState({ promedio: 0, total: 0 });
    const [progreso, setProgreso] = useState(null);
    const [vistas, setVistas] = useState([]);
    const [calificacionUsuario, setCalificacionUsuario] = useState(null);
    const [certificado, setCertificado] = useState(null);
    const [videoActivo, setVideoActivo] = useState(null);
    const [resumenIA, setResumenIA] = useState('');
    const [cargandoResumen, setCargandoResumen] = useState(false);
    const [tab, setTab] = useState('resumen');
    const [estrellasNuevas, setEstrellasNuevas] = useState(0);
    const [comentarioNuevo, setComentarioNuevo] = useState('');
    const [error, setError] = useState('');
    const [exito, setExito] = useState('');
    const [cargando, setCargando] = useState(true);
    const [modalExamen, setModalExamen] = useState(false);

    const cargarTodo = useCallback(async () => {
        try {
            const [det, prog] = await Promise.all([
                api.get(`/cursos/${id}`),
                api.get(`/cursos/${id}/progreso`).catch(() => ({ data: {} }))
            ]);
            setCurso(det.data.curso);
            setVideos(det.data.videos || []);
            setCalificaciones(det.data.calificaciones || []);
            setStats(det.data.stats || { promedio: 0, total: 0 });
            setProgreso(prog.data.inscripcion);
            setVistas(prog.data.vistas || []);
            setCalificacionUsuario(prog.data.calificacion);
            setCertificado(prog.data.certificado);
            const primero = det.data.videos?.[0];
            setVideoActivo(primero);
        } finally {
            setCargando(false);
        }
    }, [id]);

    useEffect(() => { cargarTodo(); }, [cargarTodo]);

    useEffect(() => {
        if (!videoActivo) return;
        setResumenIA('');
        setCargandoResumen(true);
        api.get(`/cursos/videos/${videoActivo.id}/resumen`)
            .then(r => setResumenIA(r.data.resumen))
            .catch(() => setResumenIA('No se pudo generar el resumen.'))
            .finally(() => setCargandoResumen(false));
    }, [videoActivo]);

    const inscribirse = async () => {
        try {
            await api.post(`/cursos/${id}/inscribirse`);
            cargarTodo();
        } catch (err) {
            setError(err.response?.data?.error || 'Error inscribiendose');
        }
    };

    const completarVideo = async () => {
        if (!videoActivo) return;
        setError('');
        try {
            await api.post(`/cursos/${id}/videos/${videoActivo.id}/completar`, { segundos: videoActivo.duracion_segundos });
            setExito('Leccion marcada como completada');
            setTimeout(() => setExito(''), 2000);
            await cargarTodo();
            const idxActual = videos.findIndex(v => v.id === videoActivo.id);
            if (idxActual < videos.length - 1) {
                setVideoActivo(videos[idxActual + 1]);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo completar');
        }
    };

    const enviarCalificacion = async (e) => {
        e.preventDefault();
        setError(''); setExito('');
        if (!estrellasNuevas) { setError('Selecciona estrellas'); return; }
        if (comentarioNuevo.trim().length < 5) { setError('Comentario requerido (min 5)'); return; }
        try {
            await api.post(`/cursos/${id}/calificar`, { estrellas: estrellasNuevas, comentario: comentarioNuevo });
            setExito('Gracias por calificar');
            setEstrellasNuevas(0);
            setComentarioNuevo('');
            cargarTodo();
        } catch (err) {
            setError(err.response?.data?.error || 'Error');
        }
    };

    const eliminarMiComentario = async () => {
        if (!window.confirm('¿Eliminar tu comentario? Podras volver a comentar despues.')) return;
        try {
            await api.delete(`/cursos/${id}/calificar`);
            setExito('Comentario eliminado');
            cargarTodo();
        } catch (err) {
            setError(err.response?.data?.error || 'Error eliminando');
        }
    };

    const generarCertificado = async () => {
        setError(''); setExito('');
        try {
            const res = await api.post(`/cursos/${id}/certificado`);
            setCertificado(res.data.certificado);
            setExito('Certificado generado');
        } catch (err) {
            setError(err.response?.data?.error || 'Error generando certificado');
        }
    };

    if (cargando) return <div className="loader"><div className="spinner" /><span>Cargando curso...</span></div>;
    if (!curso) return <div className="card">Curso no encontrado</div>;

    const idsCompletados = new Set(vistas.filter(v => v.completado).map(v => v.video_id));
    const inscrito = !!progreso;
    const completado = progreso?.completado;
    const yaCalificado = !!calificacionUsuario;

    function videoBloqueado(video) {
        if (!inscrito) return true;
        if (video.secuencia === 1) return false;
        const previos = videos.filter(v => v.secuencia < video.secuencia);
        return previos.some(p => !idsCompletados.has(p.id));
    }

    function esVideoLocal(url) {
        if (!url) return false;
        return url.startsWith('/uploads/') || /\.(mp4|webm|ogg|mov)$/i.test(url);
    }

    function urlEmbedible(url) {
        if (!url) return '';
        if (url.includes('youtube.com/watch')) {
            const m = url.match(/v=([^&]+)/);
            return m ? `https://www.youtube.com/embed/${m[1]}` : url;
        }
        if (url.includes('youtu.be/')) {
            const id = url.split('youtu.be/')[1].split('?')[0];
            return `https://www.youtube.com/embed/${id}`;
        }
        return url;
    }

    return (
        <div className="curso-vista contenido-pad">
            <div>
                {!inscrito ? (
                    <div className="card" style={{ textAlign: 'center', padding: 40 }}>
                        <h2 style={{ marginTop: 0 }}>{curso.nombre}</h2>
                        <p style={{ color: 'var(--texto-suave)' }}>{curso.descripcion}</p>
                        <p style={{ marginTop: 20 }}>
                            <FiUser /> {curso.profesor_nombre || 'Sin profesor'} &middot; {videos.length} clases &middot; {curso.duracion_horas}h
                        </p>
                        <button className="btn btn-primario" onClick={inscribirse} style={{ marginTop: 16 }}>
                            Inscribirme al curso <FiArrowRight />
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="video-wrapper">
                            {videoActivo && (
                                esVideoLocal(videoActivo.video_url) ? (
                                    <video
                                        key={videoActivo.id}
                                        src={`${RUTA_ARCHIVOS}${videoActivo.video_url}`}
                                        controls
                                        controlsList="nodownload"
                                        style={{ width: '100%', height: '100%', background: '#000' }}
                                        onEnded={() => { if (!idsCompletados.has(videoActivo.id)) completarVideo(); }}
                                    />
                                ) : (
                                    <iframe
                                        src={urlEmbedible(videoActivo.video_url)}
                                        title={videoActivo.titulo}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                    />
                                )
                            )}
                        </div>

                        <div className="video-info">
                            {error && <div className="error-msg">{error}</div>}
                            {exito && <div className="exito-msg">{exito}</div>}

                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                                <div>
                                    <div style={{ fontSize: 12, color: 'var(--texto-debil)' }}>
                                        Clase {videoActivo?.secuencia} de {videos.length}
                                    </div>
                                    <h2 style={{ margin: '4px 0 8px' }}>{videoActivo?.titulo}</h2>
                                    <div style={{ color: 'var(--texto-suave)', fontSize: 13 }}>
                                        <FiClock /> {Math.round((videoActivo?.duracion_segundos || 0) / 60)} min
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    {videoActivo && idsCompletados.has(videoActivo.id) ? (
                                        <span className="btn" style={{ background: 'var(--exito)', color: 'white' }}>
                                            <FiCheckCircle /> Completado
                                        </span>
                                    ) : (
                                        <button className="btn btn-primario" onClick={completarVideo}>
                                            <FiCheckCircle /> Marcar completado
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div style={{ marginTop: 18 }}>
                                <div style={{ fontSize: 13, color: 'var(--texto-suave)', marginBottom: 6 }}>
                                    Progreso del curso: {Math.round(progreso?.progreso_porcentaje || 0)}%
                                </div>
                                <div className="barra-progreso">
                                    <div className="barra-progreso-fill" style={{ width: `${progreso?.progreso_porcentaje || 0}%` }} />
                                </div>
                            </div>

                            <div className="tabs" style={{ marginTop: 20 }}>
                                <button className={tab === 'resumen' ? 'activa' : ''} onClick={() => setTab('resumen')}>
                                    <FiZap /> Resumen IA
                                </button>
                                <button className={tab === 'comentarios' ? 'activa' : ''} onClick={() => setTab('comentarios')}>
                                    <FiMessageCircle /> Comentarios ({calificaciones.length})
                                </button>
                                <button className={tab === 'profesor' ? 'activa' : ''} onClick={() => setTab('profesor')}>
                                    <FiUser /> Profesor
                                </button>
                            </div>

                            {tab === 'resumen' && (
                                <div>
                                    {cargandoResumen ? (
                                        <div className="loader" style={{ padding: 20 }}><div className="spinner" /><span>Generando resumen con IA...</span></div>
                                    ) : (
                                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: 'var(--texto-suave)' }}>
                                            {resumenIA}
                                        </div>
                                    )}
                                </div>
                            )}

                            {tab === 'comentarios' && (
                                <div>
                                    {calificaciones.length === 0 ? (
                                        <p style={{ color: 'var(--texto-suave)' }}>Aun no hay comentarios sobre este curso</p>
                                    ) : calificaciones.map((c, i) => (
                                        <div key={c.id || i} className="comentario">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <span className="autor">{c.nombre} {c.apellido_paterno}</span>
                                                    <span className="fecha">{new Date(c.fecha_creacion).toLocaleDateString('es-MX')}</span>
                                                </div>
                                                {c.usuario_id === usuario?.id && (
                                                    <button
                                                        className="icon-btn"
                                                        title="Eliminar mi comentario"
                                                        style={{ color: 'var(--error)' }}
                                                        onClick={eliminarMiComentario}
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                            <Estrellas valor={c.estrellas} soloLectura tamano={14} />
                                            <div className="texto">{c.comentario}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {tab === 'profesor' && (
                                <div>
                                    <h3 style={{ margin: '0 0 6px' }}>{curso.profesor_nombre}</h3>
                                    <p style={{ color: 'var(--acento)', margin: '0 0 12px', fontSize: 14 }}>{curso.especialidad}</p>
                                    <p style={{ color: 'var(--texto-suave)' }}>{curso.profesor_bio || 'Sin biografia'}</p>
                                </div>
                            )}
                        </div>

                        {completado && (
                            <div className="card" style={{ marginTop: 16 }}>
                                <h3><FiAward /> ¡Curso Completado!</h3>
                                {!yaCalificado ? (
                                    <form onSubmit={enviarCalificacion}>
                                        <p style={{ color: 'var(--texto-suave)' }}>Por favor califica este curso (obligatorio para tu certificado).</p>
                                        <div className="form-group">
                                            <label>Tu calificacion</label>
                                            <Estrellas valor={estrellasNuevas} onCambio={setEstrellasNuevas} />
                                        </div>
                                        <div className="form-group">
                                            <label>Tu comentario</label>
                                            <textarea
                                                className="input"
                                                rows={3}
                                                value={comentarioNuevo}
                                                onChange={e => setComentarioNuevo(e.target.value)}
                                                placeholder="Cuentanos que te parecio el curso..."
                                            />
                                        </div>
                                        <button type="submit" className="btn btn-primario">Enviar calificacion</button>
                                    </form>
                                ) : (
                                    <div>
                                        <p style={{ color: 'var(--exito)' }}>Ya calificaste este curso ({calificacionUsuario.estrellas} estrellas)</p>
                                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                            {!certificado ? (
                                                <button className="btn btn-primario" onClick={generarCertificado}>
                                                    <FiAward /> Generar mi certificado
                                                </button>
                                            ) : (
                                                <a className="btn btn-primario" href={`${RUTA_ARCHIVOS}${certificado.archivo_url}`} target="_blank" rel="noreferrer">
                                                    <FiDownload /> Descargar certificado
                                                </a>
                                            )}
                                            <button className="btn btn-fantasma" onClick={() => setModalExamen(true)}>
                                                Tomar examen final
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            <aside>
                <div className="lista-lecciones">
                    <div className="header">
                        <div>{curso.nombre}</div>
                        <div style={{ fontSize: 12, color: 'var(--texto-suave)', marginTop: 4, fontWeight: 'normal' }}>
                            <Estrellas valor={Math.round(stats.promedio)} soloLectura tamano={12} /> {Number(stats.promedio).toFixed(1)} ({stats.total})
                        </div>
                    </div>
                    {videos.map(v => {
                        const bloq = videoBloqueado(v);
                        const comp = idsCompletados.has(v.id);
                        const activo = videoActivo?.id === v.id;
                        return (
                            <div
                                key={v.id}
                                className={`leccion-item ${activo ? 'activa' : ''} ${bloq ? 'bloqueada' : ''} ${comp ? 'completada' : ''}`}
                                onClick={() => !bloq && setVideoActivo(v)}
                            >
                                <div className="num">
                                    {comp ? <FiCheckCircle size={14} /> : bloq ? <FiLock size={12} /> : v.secuencia}
                                </div>
                                <div className="titulo">{v.titulo}</div>
                                <div className="duracion">{Math.round((v.duracion_segundos || 0) / 60)}m</div>
                            </div>
                        );
                    })}
                </div>
            </aside>

            {modalExamen && <ModalExamen cursoId={id} cerrar={() => setModalExamen(false)} />}
        </div>
    );
}

function ModalExamen({ cursoId, cerrar }) {
    const [preguntas, setPreguntas] = useState([]);
    const [respuestas, setRespuestas] = useState({});
    const [resultado, setResultado] = useState(null);
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        api.get(`/cursos/${cursoId}/examen`).then(r => setPreguntas(r.data.preguntas || [])).finally(() => setCargando(false));
    }, [cursoId]);

    const enviar = async () => {
        const res = await api.post(`/cursos/${cursoId}/examen`, { respuestas });
        setResultado(res.data);
    };

    return (
        <div className="modal-bg">
            <div className="modal" style={{ maxWidth: 720 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2>Examen Final</h2>
                    <button className="btn btn-fantasma" onClick={cerrar}>Cerrar</button>
                </div>
                {cargando && <div className="loader"><div className="spinner" /><span>Generando examen con IA...</span></div>}
                {resultado ? (
                    <div style={{ textAlign: 'center', padding: 24 }}>
                        <h2 style={{ color: resultado.aprobado ? 'var(--exito)' : 'var(--error)' }}>
                            {resultado.aprobado ? '¡Aprobaste!' : 'No aprobaste, intenta de nuevo'}
                        </h2>
                        <p>Puntaje: <b>{Math.round(resultado.puntaje)}%</b> ({resultado.aciertos}/{resultado.total})</p>
                        <button className="btn btn-primario" onClick={cerrar}>Cerrar</button>
                    </div>
                ) : (
                    <div>
                        {preguntas.map((p, i) => (
                            <div key={p.id} className="examen-pregunta">
                                <div className="num">Pregunta {i + 1}</div>
                                <div className="texto">{p.pregunta}</div>
                                {['a', 'b', 'c', 'd'].map(letra => (
                                    <label
                                        key={letra}
                                        className={`examen-opcion ${respuestas[p.id] === letra.toUpperCase() ? 'seleccionada' : ''}`}
                                    >
                                        <input
                                            type="radio"
                                            name={`q-${p.id}`}
                                            checked={respuestas[p.id] === letra.toUpperCase()}
                                            onChange={() => setRespuestas({ ...respuestas, [p.id]: letra.toUpperCase() })}
                                        />
                                        <b>{letra.toUpperCase()})</b> {p[`opcion_${letra}`]}
                                    </label>
                                ))}
                            </div>
                        ))}
                        {preguntas.length > 0 && (
                            <button className="btn btn-primario btn-bloque" onClick={enviar} style={{ marginTop: 16 }}>
                                Enviar respuestas
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
